import React from "react";
import logo from "../../img/logoWithTextBlue.svg";

interface ShowroomCatalogViewProps {
  viewedFolder: any;
  withPrices: boolean;
  isAdmin: boolean;
  user: any;
}

const ShowroomCatalogView: React.FC<ShowroomCatalogViewProps> = ({ viewedFolder, withPrices, isAdmin, user }) => {
  const canSeeAll = isAdmin || user?.category === "gold";

  const renderBlurredDetails = (content: any) => {
    if (canSeeAll || withPrices || !content) return content;
    const isPrice = typeof content === "string" && (content.includes("€") || content.includes("$"));
    const placeholder = isPrice ? "XX.XX €" : "Restricted Information";
    return (
      <span style={{ filter: "blur(5px)", userSelect: "none" }}>
        {placeholder}
      </span>
    );
  };

  const getGenderName = (id: any) => {
    switch (parseInt(id)) {
      case 1: return "MEN";
      case 2: return "WOMEN";
      case 3: return "UNISEX";
      case 4: return "KIDS";
      case 5: return "BOYS";
      case 6: return "GIRLS";
      default: return "";
    }
  };

  return (
    <div className="showroom_catalog_container">
      {viewedFolder.products && viewedFolder.products.map((product: any, idx: number) => (
        <div className="showroom_item_card" key={product.id}>
          <div className="showroom_content_wrapper">
            {/* Images Left */}
            <div className="showroom_images_section">
              <div className="showroom_watermark_container">
                <div className="showroom_watermark_text">COLLECTION BY DEMCO ID</div>
              </div>
              <div className="showroom_img_container dual_view">
                <img 
                  src={process.env.REACT_APP_STORAGE_URL + (product.front_image || (product.images?.[0]?.path))} 
                  alt="Front View" 
                />
                <img 
                  src={process.env.REACT_APP_STORAGE_URL + (product.back_image || (product.images?.[1]?.path || product.images?.[0]?.path))} 
                  alt="Back View" 
                />
              </div>
            </div>

            {/* Details Right */}
            <div className="showroom_details_section">
              <div className="showroom_main_header">
                <h1 className="ref_code">{product.code} - {getGenderName(product.gender_id)}</h1>
                <h2 className="ref_name">{product.name}</h2>
              </div>

              <p className="showroom_description_text">
                {product.description || "High-quality sustainable garment designed for durability and comfort."}
              </p>

              {/* FABRIC SECTION */}
              <div className="showroom_data_block">
                <h3>FABRIC</h3>
                {product.fabrics && product.fabrics.length > 0 ? (
                  product.fabrics.map((fabric: any, fIdx: number) => (
                    <div key={fIdx} className="fabric_item_block">
                      {withPrices && (
                        <p className="fabric_ref_name">
                          {fabric.fabric_mill?.name} {fabric.name} {fabric.color && `- ${fabric.color}`}
                        </p>
                      )}
                      <table className="ref_info_table">
                        <tbody>
                          <tr>
                            <td><span className="label">Fabric Type : </span>{fabric.fabric_types?.name || (product.fabric_type === 'knit' ? 'Knit' : 'Denim')}</td>
                            <td><span className="label">Weight : </span>{fabric.weight_value ? (fabric.weight_value.toLowerCase().includes('oz') ? fabric.weight_value : `${fabric.weight_value} Oz`) : '-'}</td>
                            <td><span className="label">Elasticity : </span>{fabric.elasticity_value ? `${fabric.elasticity_value} %` : '0 %'}</td>
                          </tr>
                          <tr>
                            <td colSpan={withPrices ? 2 : 3}><span className="label">Composition : </span>{fabric.composition_value || 'N/A'}</td>
                            {withPrices && (
                              <td>
                                <span className="label">Price : </span>
                                {renderBlurredDetails(`${fabric.price ? Math.fround(fabric.price).toFixed(2) : (fabric.price_kg || '-')} €`)}
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ))
                ) : (
                  <p className="no_data">No Available</p>
                )}
              </div>

              {/* TECHNIQUES SECTION */}
              {product.technics && product.technics.length > 0 && (
                <div className="showroom_data_block">
                  <h3>TECHNIQUE</h3>
                  <table className="ref_info_table">
                    <tbody>
                      {product.technics.map((t: any, tIdx: number) => (
                        <tr key={tIdx}>
                          <td colSpan={withPrices ? 1 : 2}>
                            <span className="label">Technique Type : </span>{t.name}
                          </td>
                          {withPrices && (
                            <td style={{ width: '40%' }}>
                              <span className="label">Price : </span>
                              {renderBlurredDetails(`${t.pivot?.technic_price ? Math.fround(t.pivot.technic_price).toFixed(2) : '-'} €`)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ACCESSORIES SECTION */}
              {product.accessories && product.accessories.length > 0 && (
                <div className="showroom_data_block">
                  <h3>ACCESSORIES</h3>
                  <table className="ref_info_table">
                    <tbody>
                      {product.accessories.map((acc: any, aIdx: number) => (
                        <tr key={aIdx}>
                          <td colSpan={withPrices ? 1 : 2}>
                            <span className="label">Accessory Type : </span>{acc.name}
                          </td>
                          {withPrices && (
                            <td style={{ width: '40%' }}>
                              <span className="label">Price : </span>
                              {renderBlurredDetails(`${acc.price ? Math.fround(acc.price).toFixed(2) : '-'} €`)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* WASH SECTION */}
                <div className="showroom_data_block">
                  <h3>WASH</h3>
                  <table className="ref_info_table">
                    <tbody>
                      {product.washes && product.washes.slice(0, 1).map((w: any, idx: number) => (
                        <tr key={idx}>
                          <td colSpan={withPrices ? 1 : 2}>
                            <span className="label">Eim Score : </span>{w.pivot?.eim_score || w.eim_score || "25"}
                          </td>
                          {withPrices && (
                            <td style={{ width: '40%' }}>
                              <span className="label">Price : </span>
                              {renderBlurredDetails(`${w.pivot?.wash_price ? Math.fround(w.pivot.wash_price).toFixed(2) : '-'} €`)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              {/* TOTAL PRICE BLOCK (Only if prices enabled) */}
              {withPrices && (
                <div className="showroom_data_block total_price_block" style={{ marginTop: 'auto', borderTop: '2px dashed #00468c', paddingTop: '15px' }}>
                  {product.total_price && (
                    <table className="info_table" style={{ border: 'none', width: '100%' }}>
                      <tbody>
                        <tr style={{ background: '#f0f7ff' }}>
                          <td style={{ border: 'none', fontSize: '18px', fontWeight: 900, color: '#00468c' }}>
                            TOTAL PRICE :
                          </td>
                          <td style={{ border: 'none', fontSize: '20px', fontWeight: 900, color: '#00468c', textAlign: 'right' }}>
                            {renderBlurredDetails(`${Math.fround(product.total_price).toFixed(2)} €`)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ShowroomCatalogView;
